import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("wd_token");
  // Never clobber an Authorization header set explicitly by the caller
  // (e.g. the short-lived select_token used by /auth/select-tenant).
  const headers = config.headers || {};
  const hasExplicitAuth =
    headers.Authorization ||
    headers.authorization ||
    (typeof headers.has === "function" && headers.has("Authorization"));
  if (token && !hasExplicitAuth) {
    config.headers = headers;
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints report 401 as a normal "wrong credentials"/"not logged in yet"
// result, not a session expiry — never redirect for those.
const AUTH_PROBE_PATHS = ["/auth/login", "/auth/register", "/auth/me", "/auth/select-tenant", "/auth/switch-tenant"];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = (error.config?.url || "").split("?")[0];
      const isAuthProbe = AUTH_PROBE_PATHS.some((p) => path.endsWith(p));
      if (!isAuthProbe && localStorage.getItem("wd_token")) {
        localStorage.removeItem("wd_token");
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export const listCompanies    = ()              => api.get("/admin/companies").then((r) => r.data);
export const createCompany    = (payload)       => api.post("/admin/companies", payload).then((r) => r.data);
export const setCompanyStatus = (id, status)    => api.patch(`/admin/companies/${id}`, { status }).then((r) => r.data);
export const listIndustries   = ()              => api.get("/admin/industries").then((r) => r.data);

export const updateCompany = (payload) => api.patch("/company", payload).then((r) => r.data);
export const listInvites = () => api.get("/invites").then((r) => r.data);
export const revokeInvite = (id) => api.delete(`/invites/${id}`).then((r) => r.data);
export const reorderMasters = (kind, ids) => api.put(`/masters/${kind}/reorder`, { ids }).then((r) => r.data);
export const reorderRequestTypes = (ids) => api.put("/request-types/reorder", { ids }).then((r) => r.data);
export const reorderStatuses = (ids) => api.put("/ticket-statuses/reorder", { ids }).then((r) => r.data);
export const createInvite = (payload) => api.post("/invites", payload).then((r) => r.data);
export const adminResetPassword = (id) => api.post(`/users/${id}/reset-password`).then((r) => r.data);
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const selectTenant = (tenantId, selectToken, remember = false) =>
  api.post("/auth/select-tenant", { tenant_id: tenantId, remember }, { headers: { Authorization: `Bearer ${selectToken}` } }).then((r) => r.data);
export const switchTenant = (tenantId) => api.post("/auth/switch-tenant", { tenant_id: tenantId }).then((r) => r.data);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
